import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { Field, Formik, useFormikContext, Form } from 'formik';
import { Button } from 'primereact/button';
import * as Yup from 'yup';
import {
  DropdownWithValidation,
  InputPasswordWithValidationMessage,
  InputTextWithValidationMessage,
} from '../../../components/prime-formik';
import { UserAgreementCheckbox } from '../../../components/AgreementCheckboxes';

const messages = defineMessages({
  firstNameRequired: { id: 'register.first_name_required', defaultMessage: 'First name is required' },
  lastNameRequired: { id: 'register.last_name_required', defaultMessage: 'Last name is required' },
  emailRequired: { id: 'register.email_required', defaultMessage: 'Email is required' },
  passwordRequired: {
    id: 'login.password_required',
    defaultMessage: 'Password is required',
  },
  passwordConfirmationRequired: {
    id: 'login.password_confirmation_required',
    defaultMessage: 'Password confirmation is required',
  },
  passwordConfirmationDoesNotMatch: {
    id: 'login.password_confirmation_does_not_match',
    defaultMessage: 'Password confirmation does not match',
  },
  languageRequired: { id: 'register.language_required', defaultMessage: 'Language is required' },
  countryRequired: { id: 'register.country_required', defaultMessage: 'Country is required' },
  timeZoneRequired: { id: 'register.time_zone_required', defaultMessage: 'Time zone is required' },
  firstName: { id: 'register.first_name', defaultMessage: 'First name' },
  lastName: { id: 'register.last_name', defaultMessage: 'Last name' },
  email: { id: 'register.email', defaultMessage: 'Email' },
  password: { id: 'register.password', defaultMessage: 'Password' },
  confirmPassword: { id: 'register.confirm_password', defaultMessage: 'Confirm password' },
  language: { id: 'register.language', defaultMessage: 'Language' },
  country: { id: 'register.country', defaultMessage: 'Country' },
  timZone: { id: 'register.time_zone', defaultMessage: 'Time zone' },
  decline: { id: 'actions.decline', defaultMessage: 'Decline' },
  next: { id: 'actions.next', defaultMessage: 'Next' },
});

const OwnerRegistrationStep = ({ countries, locales, timeZones, token }) => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const { setFieldValue, values } = useFormikContext();

  const initialValues = {
    owner: {
      firstName: values.owner.firstName,
      lastName: values.owner.lastName,
      email: values.owner.email,
      password: values.owner.password,
      confirmPassword: values.owner.confirmPassword,
      locale: values.owner.locale,
      country: values.owner.country,
      timeZone: values.owner.timeZone,
    },
    user_terms_of_use_agreement: values.user_terms_of_use_agreement,
  };

  const validationSchema = Yup.object().shape({
    owner: Yup.object().shape({
      firstName: Yup.string().required(formatMessage(messages.firstNameRequired)),
      lastName: Yup.string().required(formatMessage(messages.lastNameRequired)),
      email: Yup.string().required(formatMessage(messages.emailRequired)),
      password: Yup.string().required(formatMessage(messages.passwordRequired)),
      confirmPassword: Yup.string()
        .when('owner.password', {
          is: (val) => !!(val && val.length > 0),
          then: Yup.string().oneOf(
            [Yup.ref('owner.password')],
            formatMessage(messages.passwordConfirmationDoesNotMatch),
          ),
        })
        .required(formatMessage(messages.passwordConfirmationRequired)),
      locale: Yup.string().required(formatMessage(messages.languageRequired)),
      country: Yup.string().required(formatMessage(messages.countryRequired)),
      timeZone: Yup.string().required(formatMessage(messages.timeZoneRequired)),
    }),
    user_terms_of_use_agreement: Yup.boolean()
      .required()
      .oneOf([true])
      .default(false),
  });

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        setFieldValue('owner', { ...values.owner });
        setFieldValue('user_terms_of_use_agreement', values.user_terms_of_use_agreement);

        history.push(`/account-invitation/organisation-details?token=${token}`);
      }}
    >
      {({ isValid, isSubmitting, handleSubmit }) => {
        const isNextDisabled = !isValid || isSubmitting;

        return (
          <Form onSubmit={handleSubmit}>
            <div className="p-col-12">
              <FormattedMessage
                id="accept-account-invitation.owner-registration.heading"
                defaultMessage="Owner registration"
                tagName="h2"
              />
              <FormattedMessage
                id="accept-account-invitation.owner-registration.instructions"
                defaultMessage="Please enter below the required personal information and preferences."
                tagName="p"
              />
            </div>
            <div className="p-col-12">
              <Field
                name="owner.firstName"
                labelText={formatMessage(messages.firstName)}
                component={InputTextWithValidationMessage}
              />
            </div>
            <div className="p-col-12">
              <Field
                name="owner.lastName"
                labelText={formatMessage(messages.lastName)}
                component={InputTextWithValidationMessage}
              />
            </div>
            <div className="p-col-12">
              <Field
                name="owner.email"
                labelText={formatMessage(messages.email)}
                component={InputTextWithValidationMessage}
              />
            </div>
            <div className="p-col-12">
              <Field
                name="owner.password"
                labelText={formatMessage(messages.password)}
                component={InputPasswordWithValidationMessage}
              />
            </div>
            <div className="p-col-12">
              <Field
                name="owner.confirmPassword"
                labelText={formatMessage(messages.confirmPassword)}
                component={InputPasswordWithValidationMessage}
              />
            </div>
            <div className="p-col-12">
              <label className="field-label" htmlFor="language">
                <FormattedMessage id="register.language" defaultMessage="Language" />
              </label>
              <Field
                id="owner.locale"
                name="owner.locale"
                labelText={formatMessage(messages.language)}
                filter
                filterBy="label,value"
                options={locales}
                component={DropdownWithValidation}
              />
            </div>
            <div className="p-col-12">
              <label className="field-label" htmlFor="owner.country">
                <FormattedMessage id="register.country" defaultMessage="Country" />
              </label>
              <Field
                id="owner.country"
                name="owner.country"
                labelText={formatMessage(messages.country)}
                filter
                filterBy="label,value"
                options={countries}
                component={DropdownWithValidation}
              />
            </div>
            <div className="p-col-12">
              <label className="field-label" htmlFor="owner.timeZone">
                <FormattedMessage id="register.time_zone" defaultMessage="Time zone" />
              </label>
              <Field
                id="owner.timeZone"
                name="owner.timeZone"
                labelText={formatMessage(messages.timZone)}
                filter
                filterBy="label,value"
                options={timeZones}
                component={DropdownWithValidation}
              />
            </div>
            <div className="p-col-12">
              <Field
                id="user_terms_of_use_agreement"
                name="user_terms_of_use_agreement"
                component={UserAgreementCheckbox}
              />
            </div>
            <div>
              <Button
                type="button"
                label={formatMessage(messages.decline)}
                onClick={() => history.push(`/account-invitation-decline?token=${token}`)}
              />
              <Button type="submit" label={formatMessage(messages.next)} disabled={isNextDisabled} />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

OwnerRegistrationStep.propTypes = {
  countries: PropTypes.arrayOf(PropTypes.any).isRequired,
  locales: PropTypes.arrayOf(PropTypes.any).isRequired,
  timeZones: PropTypes.arrayOf(PropTypes.any).isRequired,
  token: PropTypes.string.isRequired,
};

export default OwnerRegistrationStep;
